import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import {
  AddGeoResolverMain,
  DeleteGeoResolverMain,
  fetchGeoResolverMain,
  UpdateGeoResolverMain,
} from "../../Redux/WhatsappSlice/GeoResolver";
import Loader from "../../Components/Loader/Loader";

const GeoResolverMain = () => {
  const dispatch = useDispatch();
  const { GeoResolverMainData, GeoResolverMainLoading } = useSelector(
    (state) => state.GeoResolver
  );

  const [formData, setFormData] = useState({
    Provider: "",
    Base_Url: "",
    EndPoint: "",
    Api_Key: "",
  });

  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchGeoResolverMain());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.Base_Url || formData.Base_Url.trim() === "")
      errs.Base_Url = "Base URL is required";
    if (!formData.EndPoint || formData.EndPoint.trim() === "")
      errs.EndPoint = "EndPoint is required";
    if (!formData.Api_Key || formData.Api_Key.trim() === "")
      errs.Api_Key = "API Key is required";
    return errs;
  };

  const resetForm = () => {
    setFormData({ Provider: "", Base_Url: "", EndPoint: "", Api_Key: "" });
    setEditId(null);
    setErrors({});
  };

  const handleEdit = (row) => {
    setEditId(row.MainId);
    setFormData({
      Provider: row.Provider || "",
      Base_Url: row.Base_Url || "",
      EndPoint: row.EndPoint || "",
      Api_Key: row.Api_Key || "",
    });
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await dispatch(DeleteGeoResolverMain({ id: row.MainId })).unwrap();
      toast.success("Deleted successfully!", { position: "top-right", autoClose: 1000 });
      dispatch(fetchGeoResolverMain());
    } catch (error) {
      toast.error(`Failed to delete: ${error}`, { position: "top-right", autoClose: 1500 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editId) {
        await dispatch(UpdateGeoResolverMain({ id: editId, formData })).unwrap();
        toast.success("GeoResolver Main updated successfully!", { position: "top-right", autoClose: 1000 });
      } else {
        await dispatch(AddGeoResolverMain({ values: formData })).unwrap();
        toast.success("GeoResolver Main added successfully!", { position: "top-right", autoClose: 1000 });
      }
      resetForm();
      dispatch(fetchGeoResolverMain());
    } catch (error) {
      toast.error(`Failed to ${editId ? "update" : "add"}: ${error?.message || "Unknown error"}`, {
        position: "top-right",
        autoClose: 1500,
      });
    }
  };

  const columns = [
    { field: "id", headerName: "No", flex: 0.5 },
    { field: "Provider", headerName: "Provider Name", flex: 1 },
    { field: "Base_Url", headerName: "Base URL", flex: 1.5 },
    { field: "EndPoint", headerName: "EndPoint", flex: 1 },
    { field: "Api_Key", headerName: "API Key", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => handleEdit(params.row)}
          >
            Update
          </button>
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const rows = (GeoResolverMainData || []).map((el, index) => ({
    id: index + 1,
    MainId: el._id,
    Provider: el?.Provider || "-",
    Base_Url: el?.Base_Url,
    EndPoint: el?.EndPoint,
    Api_Key: el?.Api_Key,
  }));

  const sortedRows = [...rows]
    .sort((a, b) => (a.Provider || "").localeCompare(b.Provider || "", undefined, { sensitivity: 'base' }))
    .map((item, idx) => ({ ...item, id: idx + 1 }));

  if (GeoResolverMainLoading) return <Loader />;

  return (
    <>
      <div>
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            {editId ? "Update GeoResolver Main" : "Add GeoResolver Main"}
          </h3>
        </div>

        <div className="Form_bg_color bg-white shadow-sm rounded-xl mt-4">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-12 gap-4 p-3 rounded-2xl"
          >
            {/* Provider */}
            <div className="col-span-12 lg:col-span-3 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Provider Name</h5>
              <input
                type="text"
                name="Provider"
                value={formData.Provider}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
            </div>

            {/* Base URL */}
            <div className="col-span-12 lg:col-span-3 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Base URL</h5>
              <input
                type="text"
                name="Base_Url"
                value={formData.Base_Url}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Base_Url && <p className="text-red-500 text-sm mt-1">{errors.Base_Url}</p>}
            </div>

            {/* EndPoint */}
            <div className="col-span-12 lg:col-span-3 md:col-span-6">
              <h5 className="mb-1 block font-semibold">EndPoint</h5>
              <input
                type="text"
                name="EndPoint"
                value={formData.EndPoint}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.EndPoint && <p className="text-red-500 text-sm mt-1">{errors.EndPoint}</p>}
            </div>

            {/* API Key */}
            <div className="col-span-12 lg:col-span-3 md:col-span-6">
              <h5 className="mb-1 block font-semibold">API Key</h5>
              <input
                type="text"
                name="Api_Key"
                value={formData.Api_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Api_Key && <p className="text-red-500 text-sm mt-1">{errors.Api_Key}</p>}
            </div>

            {/* Submit */}
            <div className="submit_Form col-span-12 flex justify-center">
              <button type="submit" className="All_button">
                {editId ? "Update" : "Submit"}
              </button>
              {editId && (
                <button
                  type="button"
                  className="bg-gray-500 text-white ms-2 px-4 py-2 rounded hover:bg-gray-600"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="px-2 py-6">
          <div className="title">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              View GeoResolver Main
            </h3>
          </div>
          <div className="rounded-lg overflow-x-auto mt-3">
            <WpDynamicTable columns={columns} rows={sortedRows} height="550px" />
          </div>
        </div>
      </div>
    </>
  );
};

export default GeoResolverMain;
